import { memo, useMemo } from "react"
import * as PropTypes from "prop-types"
import styled, { css } from "styled-components"
import { useLocale, useT } from "react-polyglot-hooks"
import Car from "models/car"
import { sendEvent } from "scripts/GoogleAnalytics"
import Image from "next/legacy/image"
import { color, mq } from "helpers/styled"
import Promotions from "components/car/Promotions"
import HighlightedFeature from "components/car/HighlightedFeature"
import carHasPromo from "helpers/carHaspromo"
import Typography from "../../styled/Typography"
import TagsList from "../../car/TagsList"
import Link from "../../Link"

const size = {
  sm: "290px",
  md: "325px",
  lg: "375px"
}

const display = m => css`
  display: ${({ show, index }) => (!show[m] || show[m] > index ? "block" : "none")};
`

export const Wrapper = styled.div`
  ${display("xs")};

  @media ${mq("sm")} {
    ${display("sm")};
  }

  @media ${mq("md")} {
    ${display("md")};
  }

  @media ${mq("lg")} {
    ${display("lg")};
  }

  @media ${mq("xl")} {
    ${display("xl")};
  }
`

export const Item = styled.a`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: 240px;
  margin: 0 auto;
  padding: 9px;
  border: 1px solid ${color("lightGray")};
  border-radius: 8px;
  box-shadow: 0 2px 16px 0 rgba(195, 205, 224, 0.8);
  overflow: hidden;

  ${({ featured }) =>
    featured
      ? `background: linear-gradient(-10deg, transparent 50.5%, ${color("mediumBlue")}99 51.5%), ${color("darkBlue")};`
      : `background-color: ${color("white")};`}

  ${({ blackFriday }) =>
    blackFriday
      ? `background: linear-gradient(-10deg, transparent 50.5%, ${color("blackFridayLight")}99 51.5%), ${color("blackFriday")}; background-color: ${color("blackFriday")};`
      : null}

  ${({ backgroundImg }) =>
    backgroundImg
      ? `
      background-image: url("${backgroundImg}");
      background-size: cover;
      background-repeat: no-repeat;
      background-position: center;
    `
      : null}

  @media ${mq("xs")} {
    width: ${({ width }) => (width ? `${size[width]}!important` : size.md)};
  }

  @media ${mq("lg")} {
    margin: 0;
    width: ${size.sm};

    .preview & {
      width: ${size.lg};
      min-height: 310px;
    }
  }

  @media ${mq("xl")} {
    width: ${size.lg};
    min-height: 310px;

    .with-filter & {
      width: ${size.sm};
      min-height: 240px;
    }
  }

  &:hover {
    box-shadow: none;
  }

  > * {
    position: relative;
    z-index: 1;
  }
`

const Title = styled(Typography)`
  margin-bottom: 5px;
  ${({ featured }) => (featured ? `color: ${color("white")};` : null)}
`

const ImageWrapper = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  width: calc(100% - 20px);
  transform: translate(-50%, -50%);
  opacity: ${({ sold }) => (sold ? 0.5 : null)};
  z-index: 0;

  @media ${mq("xs")} {
    top: calc(50% + 13px);
  }
`

const CarImage = styled.img`
  display: block;
  width: 100%;
  height: auto;
  object-fit: contain;
`

const DataRow = styled.div`
  display: flex;
  justify-content: space-between;
  ${({ sold }) => (sold ? "opacity: 0.5" : null)}
`

const TagsRow = styled(DataRow)`
  align-items: flex-end;
  margin-top: auto;

  > * {
    flex: 0 1 auto;
  }
`

const PriceWrap = styled.div`
  text-align: right;
`

const Price = styled(Typography)`
  font-size: 24px;
  line-height: 24px;
  margin: 0;
  ${({ featured }) => (featured ? `color: ${color("white")};` : null)}

  span {
    display: inline;
  }
`

const PriceSmall = styled(Typography)`
  line-height: 14px;
  font-size: 10px;

  span {
    color: ${color("mediumVioletRed")};
    font-size: 12px;
    line-height: 18px;
    margin-left: 10px;
    text-decoration: line-through;
  }
`
const DownPayment = styled(Typography)`
  line-height: 14px;
  font-size: 10px;
  font-style: italic;
`

const Small = styled(Typography)`
  text-transform: capitalize;
`

function CarItem({ car, index, pageName, show, withoutTaxes, eventName }) {
  const carModel = new Car(car)
  const { isEco, frontImage, modelName, sellingStatus } = carModel

  const {
    brand,
    model,
    version,
    environmentalLabel,
    fuel,
    transmission,
    promotionOldPrice,
    promotionBackgroundUrl,
    sold,
    slug,
    minimumPrice,
    minimumPriceWithoutTaxes,
    downPayment,
    blueBackground,
    blackBackground,
    brandImageUrlPath
  } = car

  const t = useT()
  const locale = useLocale()

  const hasPromo = useMemo(() => carHasPromo(car, locale), [car, locale])

  const handleClick = () => {
    const name = eventName || `Click ${sellingStatus === "pre-owned" ? "pre-owned " : ""}car detail`

    sendEvent(name, {
      event_category: pageName,
      event_label: `${brand} ${modelName}`,
      brand,
      model: modelName,
      position: index + 1,
      price: minimumPrice
    })
  }

  const slugParts = slug.split("/")
  const slugPartModel = sellingStatus === "flexible" ? slugParts[2] : slugParts[1]
  const slugPartVersion = sellingStatus === "flexible" ? slugParts[3] : slugParts[2]

  return (
    <Wrapper show={show} index={index} className={`car-item${!hasPromo ? " no-promo" : ""}`}>
      <Link
        page={sellingStatus === "flexible" ? "flexible_version" : "version"}
        params={{ brand: brandImageUrlPath, model: slugPartModel, version: slugPartVersion }}
        prefetch={false}
        rel={pageName === "pre-owned" ? "nofollow noindex" : undefined}>
        <Item
          backgroundImg={promotionBackgroundUrl}
          onClick={handleClick}
          featured={blueBackground}
          blackFriday={blackBackground}
          rel={pageName === "pre-owned" ? "nofollow noindex" : undefined}>
          <DataRow sold={sold}>
            <div>
              <Title variant="label" weight="bold" el="h3" featured={blueBackground || blackBackground}>
                {`${brand} ${modelName}`}{" "}
                <Small variant="small" color="gray">
                  {version}
                </Small>
              </Title>
              <HighlightedFeature isEco={isEco} fuel={fuel} transmission={transmission} />
              {environmentalLabel && isEco && (
                <div style={{ height: "0" }}>
                  <Image
                    alt={`Enviromental label ${environmentalLabel}`}
                    src={`/f_svg/static/etiquetas/dgt-${environmentalLabel === "0" ? "zero" : environmentalLabel}`}
                    width={32}
                    height={32}
                  />
                </div>
              )}
            </div>
            <PriceWrap>
              <PriceSmall variant="small" color="gray">
                {t("common.from")}
                {promotionOldPrice && <span>{`${promotionOldPrice} €`}</span>}
              </PriceSmall>
              <Price
                variant="title3"
                weight="medium"
                color="skyBlue"
                el="span"
                featured={blueBackground || blackBackground}>
                {`${withoutTaxes ? minimumPriceWithoutTaxes : minimumPrice}  €`}
              </Price>
              <PriceSmall variant="small" color="gray">
                {t("common.month")}
                {sellingStatus !== "flexible" && ` · ${t(`common.${withoutTaxes ? "without_tax" : "tax_included"}`)}`}
              </PriceSmall>
              {locale === "it" && (
                <DownPayment variant="small" color="skyBlue">
                  {downPayment ? t("common.down_payment", { downPayment }) : t("common.without_down_payment")}
                </DownPayment>
              )}
            </PriceWrap>
          </DataRow>
          <TagsRow>
            <TagsList limit={2} {...car} />
            <Promotions car={car} />
          </TagsRow>
          <ImageWrapper sold={sold}>
            <CarImage
              height={235}
              width={353}
              title={`Renting ${brand} ${model} ${version}`}
              alt={`Renting ${brand} ${model} ${version}`}
              src={`${process.env.NEXT_PUBLIC_CLOUDINARY}f_auto,c_limit,w_353,q_auto/${frontImage}`}
              srcSet={`${process.env.NEXT_PUBLIC_CLOUDINARY}f_auto,c_limit,w_303,q_auto/${frontImage} 303w, ${process.env.NEXT_PUBLIC_CLOUDINARY}f_auto,c_limit,w_353,q_auto/${frontImage} 353w, ${process.env.NEXT_PUBLIC_CLOUDINARY}f_auto,c_limit,w_606,q_auto/${frontImage} 606w, ${process.env.NEXT_PUBLIC_CLOUDINARY}f_auto,c_limit,w_706,q_auto/${frontImage} 706w`}
              sizes="(min-width: 1200px) 353px, (min-width: 992px) 268px, 303px"
              loading="lazy"
              decoding="async"
              onError={({ currentTarget }) => {
                const image = currentTarget

                if (!image.src.includes("not-found-image.png")) {
                  image.removeAttribute("srcset")
                  image.src = `${process.env.NEXT_PUBLIC_CLOUDINARY}f_auto,c_limit,w_750,q_auto/v1/car_model/not-found-image.png`
                }
              }}
            />
          </ImageWrapper>
        </Item>
      </Link>
    </Wrapper>
  )
}

CarItem.defaultProps = {
  show: {},
  eventName: null
}

CarItem.propTypes = {
  car: PropTypes.object.isRequired,
  show: PropTypes.object,
  index: PropTypes.number.isRequired,
  pageName: PropTypes.string.isRequired,
  withoutTaxes: PropTypes.bool.isRequired,
  eventName: PropTypes.string
}

export default memo(CarItem)
